import { debounce } from "lodash";
import { RefObject, useEffect, useState } from "react";

export const useLineClamp = <T extends HTMLElement>(
  ref: RefObject<T>,
  content: string
): boolean => {
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const checkClamp = () => {
      const original = ref.current;
      if (!original) return;

      const clone = original.cloneNode(true) as HTMLElement;

      clone.className = clone.className
        .split(" ")
        .filter((cls) => !cls.startsWith("line-clamp"))
        .join(" ");

      const rect = original.getBoundingClientRect();
      clone.style.position = "absolute";
      clone.style.visibility = "hidden";
      clone.style.pointerEvents = "none";
      clone.style.height = "auto";
      clone.style.maxHeight = "none";
      clone.style.overflow = "visible";
      clone.style.whiteSpace = "pre-wrap";
      clone.style.wordBreak = "break-word";
      clone.style.overflowWrap = "break-word";
      clone.style.width = `${rect.width}px`;

      original.parentElement?.appendChild(clone);

      const clampedHeight = original.getBoundingClientRect().height;
      const fullHeight = clone.getBoundingClientRect().height;

      setIsClamped(fullHeight > clampedHeight);
      original.parentElement?.removeChild(clone);
    };

    const debouncedCheck = debounce(checkClamp, 100);

    const raf = requestAnimationFrame(checkClamp);

    window.addEventListener("resize", debouncedCheck);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", debouncedCheck);
    };
  }, [ref, content]);

  return isClamped;
};
